/**
 * SinglePageLayout - Componente principal de layout
 * Refactorizado para usar componentes de sección y hooks personalizados
 */
import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import RetroBackground from "./RetroBackground";
import { extractChapterId, setupMobileViewport, isSmallScreen } from "../../shared/utils/helpers";
import { useComics, useSection, useChapterData } from "../../hooks";
import {
  HomeSection,
  WhoWeAreSection,
  ChaptersSection,
  ChapterOptionsSection,
  ComicViewerSection,
  ArticlesSection,
  ExtrasSection,
  OpinionsSection,
  PrivacySection,
  SubscriptionSection,
  NotFoundSection,
} from "../sections";

const SinglePageLayout = () => {
  const { i18n } = useTranslation();

  // Custom hooks para estado y navegación
  const {
    currentSection,
    isSectionValid,
    isSliderActive,
    isChapterOptions,
    isComicView,
    isArticlesView,
    isExtrasView,
    currentLanguage,
    goTo,
  } = useSection();

  const { comics } = useComics();

  // Obtener ID del capítulo actual
  const chapterId = extractChapterId(currentSection);

  // Datos del capítulo usando custom hook
  const {
    chapterTitle,
    comicImages,
    fichas,
    extraData,
    backendBaseUrl,
  } = useChapterData(comics, chapterId, currentLanguage);

  // Efectos de configuración
  useEffect(() => {
    document.title = "Zaquiel Cómic";
    document.documentElement.lang = i18n.language;
    const cleanupViewport = setupMobileViewport();
    return cleanupViewport;
  }, [i18n.language]);

  // Detectar pantalla pequeña
  const [screenIsSmall, setScreenIsSmall] = useState(false);
  useEffect(() => {
    const checkScreen = () => setScreenIsSmall(isSmallScreen());
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  return (
    <div
      className={`bg-cover bg-center flex items-center justify-center relative ${isSliderActive ? "slider-active" : ""}`}
      style={{
        height: "calc(var(--vh, 1vh) * 100)",
        backgroundImage: "url('/zaquiel-fondo-1920x1080.webp')",
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        backgroundPosition: "right center",
      }}
    >
      <RetroBackground />
      <div id="svg-container" className="svg-background fixed inset-0 pointer-events-none z-0"></div>

      {/* Overlay animado para secciones de capítulos - fade in/out suave */}
      <AnimatePresence>
        {(currentSection === "capitulos" || isChapterOptions) && (
          <motion.div
            key="chapters-overlay"
            className="absolute inset-0 bg-black bg-opacity-50 z-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {/* Secciones principales */}
        {currentSection === "home" && <HomeSection key="home" goTo={goTo} />}
        {currentSection === "quienes-somos" && <WhoWeAreSection key="quienes-somos" />}
        {currentSection === "capitulos" && (
          <ChaptersSection
            key="capitulos"
            comics={comics}
            currentLanguage={currentLanguage}
            backendBaseUrl={backendBaseUrl}
            goTo={goTo}
          />
        )}

        {/* Secciones de capítulo */}
        {isChapterOptions && (
          <ChapterOptionsSection
            key={`${currentSection}-opciones`}
            currentSection={currentSection}
            goTo={goTo}
            screenIsSmall={screenIsSmall}
          />
        )}

        {isComicView && (
          <ComicViewerSection
            key={`${currentSection}-comic`}
            currentSection={currentSection}
            comicImages={comicImages}
          />
        )}

        {isArticlesView && (
          <ArticlesSection
            key={`${currentSection}-articulos`}
            currentSection={currentSection}
            chapterTitle={chapterTitle}
            fichas={fichas}
            currentLanguage={currentLanguage}
            backendBaseUrl={backendBaseUrl}
          />
        )}

        {isExtrasView && (
          <ExtrasSection
            key={`${currentSection}-extras`}
            currentSection={currentSection}
            chapterTitle={chapterTitle}
            extraData={extraData}
          />
        )}

        {/* Otras secciones */}
        {currentSection === "opiniones" && <OpinionsSection key="opiniones" />}
        {currentSection === "privacidad" && <PrivacySection key="privacidad" />}
        {currentSection === "suscripcion" && <SubscriptionSection key="suscripcion" />}

        {/* 404 */}
        {!isSectionValid && <NotFoundSection key="404" />}
      </AnimatePresence>
    </div>
  );
};

export default SinglePageLayout;
